// @flow
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import Divider from '@material-ui/core/Divider';
import AddIcon from '@material-ui/icons/Add';
import { connect } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import { addBoostWords, setAddBoostWordsLoading, setAddBoostWordsSuccess } from '../../actions/boost';
import ValidatedMessage from '../ValidatedMessage';
import { getSnack } from '../../utils/snackbar';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
  },
  scrollPaper: {
    background: theme.palette.type === 'light' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.1)',
  },
  dialogPaper: {
    justifyContent: 'space-between',
    padding: 40,
    background: theme.palette.type === 'light' ? theme.palette.ava.white : theme.palette.ava.dark2,
    borderRadius: 20,
    maxHeight: 560,
    maxWidth: 540,
    [theme.breakpoints.down('sm')]: {
      maxHeight: '94%',
    },
    [theme.breakpoints.down('xs')]: {
      padding: 24,
      margin: 0,
    },
  },
  heading: {
    color: theme.palette.type === 'light' ? theme.palette.ava.dark1 : theme.palette.ava.white,
    fontSize: 20,
    fontWeight: 500,
    lineHeight: '26px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
      lineHeight: '18px',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  button: {
    borderRadius: 100,
    fontSize: 20,
    lineHeight: '26px',
    fontWeight: 400,
    padding: '15px 20px',
    boxShadow: '0px 4px 4px rgba(112, 144, 176, 0.25)',
    position: 'relative',
  },
  buttonMain: {
    width: 'calc(50% - 12px)',
  },
  buttonBorder: {
    border: `1px solid ${theme.palette.ava.blue2}`,
    color: theme.palette.ava.blue2,
    background: theme.palette.ava.white,
    '&:hover': {
      background: theme.palette.ava.navy5,
    },
  },
  buttonFill: {
    color: theme.palette.ava.white,
    background: theme.palette.ava.blue2,
    '&:hover': {
      backgroundColor: theme.palette.ava.blue1,
    },
  },
  divider: {
    backgroundColor: theme.palette.ava.grey6,
    marginTop: 12,
    marginBottom: 16,
  },
  wordsScroll: {
    marginRight: -16,
    paddingRight: 16,
    overflow: 'auto',
    scrollbarWidth: 'thin',

    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 12,
      background: theme.palette.ava.grey6,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: theme.palette.ava.grey5,
    },
  },
  wordContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 18,
    lineHeight: 1.3,
    height: 42,
  },
  addButton: {
    padding: '7px 20px 7px 15px',
  },
  addButtonText: {
    marginLeft: 7,
  },
  loading: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  editedWords: string[];
  addBoostWords: (words: string[], callbacks?: { onSuccess?: Function, onFailure?: Function }) => Promise<void>;
  setAddBoostWordsSuccess: (value: boolean) => void;
  setAddBoostWordsLoading: (value: boolean) => void;
  boostWords: string[];
}

interface Word {
  text: string;
  loading?: boolean;
  success?: boolean;
  timeout?: any;
}

const BoostPopup = ({
  open,
  onClose,
  editedWords,
  addBoostWords,
  boostWords,
  setAddBoostWordsSuccess,
  setAddBoostWordsLoading,
}: Props) => {
  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [words, setWords] = useState<Word[]>(editedWords.map(w => ({ text: w })));
  const [addAllClicked, setAddAllClicked] = useState(false);
  const [allTimeout, setAllTimeout] = useState();

  useEffect(() => {
    words.forEach(word => {
      if (!word.success && boostWords.map(w => w.toLowerCase()).includes(word.text.toLowerCase())) {
        word.loading = false;
        word.success = true;
        if (!addAllClicked) {
          word.timeout = setTimeout(() => {
            setWords(words => words.filter(w => w.text !== word.text));
          }, 3000);
        }
      }
    });

    if (words.every(w => w.success) && addAllClicked) {
      words.forEach(word => clearTimeout(word.timeout));
      setAllTimeout(
        setTimeout(() => {
          setWords([]);
        }, 1000)
      );
    }

    setWords([...words]);
  }, [boostWords]);

  useEffect(() => {
    if (!words || !words.length) {
      if (allTimeout) {
        clearTimeout(allTimeout);
      }
      onClose();
    }
  }, [words]);

  useEffect(() => {
    return () => {
      if (words && words.length) {
        words.forEach(word => word.timeout && clearTimeout(word.timeout));
      }
      setAddBoostWordsSuccess(false);
      setAddBoostWordsLoading(false);
    };
  }, []);

  const handleAddBoostWords = boostWords => {
    addBoostWords(boostWords, {
      onSuccess: () => {},
      onFailure: () => getSnack({ enqueueSnackbar, closeSnackbar })(t('boost.error.requestFailure')),
    });
  };

  const onAddWord = word => {
    word.loading = true;
    setWords([...words]);
    handleAddBoostWords([word.text]);
  };

  const onAddAll = () => {
    setAddAllClicked(true);
    const newWords = words.filter(w => !w.loading && !w.success).map(w => w.text);
    handleAddBoostWords(newWords);
  };

  const successMessage = t('options.added');

  return (
    <Dialog
      className={classes.root}
      classes={{ scrollPaper: classes.scrollPaper, paper: classes.dialogPaper }}
      open={open}
      onClose={onClose}
    >
      <h4 className={classes.heading}>{t('boost.dialog.heading', { count: words.length })}</h4>

      <div className={classes.wordsScroll}>
        {words.map(word => {
          let controls;

          if (word.loading) {
            controls = <CircularProgress size={16} color="primary" className={classes.wordLoading} />;
          } else if (word.success) {
            controls = <ValidatedMessage message={successMessage} />;
          } else {
            controls = (
              <Button
                className={classnames(classes.button, classes.buttonBorder, classes.addButton)}
                onClick={() => onAddWord(word)}
              >
                <AddIcon />
                <span className={classes.addButtonText}>{t('boost.dialog.add')}</span>
              </Button>
            );
          }

          return (
            <React.Fragment key={word.text}>
              <div className={classes.wordContainer}>
                <span>{word.text}</span>

                {controls}
              </div>
              <Divider className={classes.divider} />
            </React.Fragment>
          );
        })}
      </div>

      <div className={classes.buttonContainer}>
        <Button className={classnames(classes.button, classes.buttonMain, classes.buttonBorder)} onClick={onClose}>
          {t('boost.dialog.ignore')}
        </Button>
        <Button
          className={classnames(classes.button, classes.buttonMain, classes.buttonFill)}
          onClick={onAddAll}
          disabled={addAllClicked}
        >
          {t('boost.dialog.addAll')}
          {addAllClicked && <CircularProgress size={24} color="secondary" className={classes.loading} />}
        </Button>
      </div>
    </Dialog>
  );
};

const mapStateToProps = ({ scribeSession: { boostWords } }) => ({ boostWords });

const mapDispatchToProps = {
  addBoostWords,
  setAddBoostWordsSuccess,
  setAddBoostWordsLoading,
};

export default connect(mapStateToProps, mapDispatchToProps)(BoostPopup);
